import React, { useEffect } from 'react'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { DSButton, DSText, DSSpacer } from '@zoe/ds-web'
import { ScreenContainer } from '../core/layout/ScreenContainer'
import { StickyFooter, StickyFooterContent } from '../core/layout/StickyFooter'
import { onNextClickType } from '../types'
import { QuizBase } from '../../redux/quiz/types'
import { Answer, submitAnswers } from '../../redux/quiz/slice'
import { AppDispatch } from '../../App'
import { trackQuizQuestionAnswered } from '../gtmV2'
import { getQuizRouteConfig } from '../versioned-components/v5/quizRouting'
import DEFAULT_QUIZ_CONFIG from '../../config/quiz.config'
import { VISITED_SYMPTOM_RESULTS_KEY } from './SymptomScoreCalculatingScreen'
import {
  calculateSymptomScore,
  getSymptomScoreBucket,
  getTopSymptoms,
  SymptomScoreBucket,
  SymptomSeverity,
} from '../../libs/symptomScore/utils'

const SCORE_BUCKET_COPY: Record<SymptomScoreBucket, { headline: string; body: string }> = {
  '0-24': {
    headline: 'high level of symptoms',
    body: 'We’ll show you the right foods to eat for your specific body, so you can start feeling better within weeks.',
  },
  '25-49': {
    headline: 'moderately high level of symptoms',
    body: 'We’ll show you the right foods to eat for your specific body, so you can start feeling better within weeks.',
  },
  '50-74': {
    headline: 'moderate level of symptoms',
    body: 'We’ll show you the right foods to eat for your specific body, so you can start feeling better within weeks.',
  },
  '75-100': {
    headline: 'low level of symptoms',
    body: 'We’ll show you how to eat real foods that fuel and nourish your body, and keep you feeling your best.',
  },
}

const SCORE_BAR_THRESHOLDS: SymptomScoreBucket[] = ['0-24', '25-49', '50-74', '75-100']
const SCORE_BAR_COLOURS: Record<SymptomScoreBucket, string> = {
  '0-24': '#F2785C',
  '25-49': '#F2B33D',
  '50-74': '#7DBE6B',
  '75-100': '#3DAA61',
}

const SEVERITY_COLOURS: Record<SymptomSeverity, { background: string; color: string }> = {
  High: { background: '#F2785C', color: '#0E0F0C' },
  Average: { background: '#F2B33D', color: '#0E0F0C' },
  Low: { background: '#3DAA61', color: '#0E0F0C' },
}

const Card = styled.div`
  background: var(--colour-surface-cream-subtle);
  border-radius: 16px;
  padding: 24px 16px;
  text-align: center;
`

const BarRow = styled.div`
  display: flex;
  gap: 8px;
  position: relative;
`

const BarSegment = styled.div<{ $background: string }>`
  flex: 1;
  height: 12px;
  border-radius: 9999px;
  background: ${({ $background }) => $background};
`

const BarMarker = styled.div<{ $bandIndex: number; $fraction: number }>`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  /* Position the marker inside its band's segment (4 segments, 8px gaps) so a
     score at a band boundary (e.g. 50) sits on the band, not in the gap. */
  left: ${({ $bandIndex, $fraction }) =>
    `calc(${$bandIndex} * ((100% - 24px) / 4 + 8px) + ${$fraction} * ((100% - 24px) / 4))`};
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ffffff;
  border: 2px solid #1b1b1f;
`

const BarLabelsRow = styled.div`
  display: flex;
  justify-content: space-between;
`

const SymptomRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--colour-surface-cream-subtle);
  border-radius: 12px;
  padding: 16px;
  gap: 12px;
`

const SeverityBadge = styled.span<{ $background: string; $color: string }>`
  display: inline-block;
  padding: 6px 14px;
  border-radius: 9999px;
  background: ${({ $background }) => $background};
  color: ${({ $color }) => $color};
  font-size: 14px;
  font-weight: 400;
  flex-shrink: 0;
`

export type SymptomScoreResultsScreenProps = {
  buttonText?: string
  disclaimer?: string
}

type Props = SymptomScoreResultsScreenProps & {
  onNextClick: onNextClickType
  quiz: QuizBase
}

export const SymptomScoreResultsScreen: React.FC<Props> = ({
  buttonText = 'Continue',
  disclaimer = 'Your score is for general information only and is not a medical assessment or diagnosis.',
  onNextClick,
  quiz,
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const location = useLocation()
  const { quizVersion } = getQuizRouteConfig(location.pathname)
  const productType = DEFAULT_QUIZ_CONFIG[quizVersion]?.productType

  const score = calculateSymptomScore(quiz)
  const bucket = getSymptomScoreBucket(score)
  const topSymptoms = getTopSymptoms(quiz)
  const copy = SCORE_BUCKET_COPY[bucket]

  // Persist the derived symptom score and fire a `quiz_question_answered`
  // event for each derived answer, mirroring the Menoscale results pattern in
  // ScreenRoutes.onEmailSubmit. The values are deterministic from the slider
  // answers already in state, so it's safe to run once on mount.
  useEffect(() => {
    sessionStorage.setItem(VISITED_SYMPTOM_RESULTS_KEY, 'true')
    const mainSymptoms = topSymptoms.map((s) => s.key)
    const answers: Answer[] = [
      { question_key: 'symptom_score', answer: String(score) },
      { question_key: 'symptom_score_category', answer: bucket },
      { question_key: 'symptom_score_main_symptoms', answer: mainSymptoms },
    ]
    dispatch(submitAnswers({ quiz_version: quizVersion, answers }))
    answers.forEach((a) => {
      const value = Array.isArray(a.answer) ? a.answer.toString() : a.answer
      trackQuizQuestionAnswered(quizVersion, productType, a.question_key, value)
    })
    // We intentionally fire this exactly once per mount.
  }, [])

  // Place the marker inside the band its score belongs to (4 bands of 25
  // points). Pad the within-band fraction so boundary scores (e.g. 50 → start
  // of the light-green band) sit clearly inside the band rather than
  // straddling the gap between segments.
  const MARKER_EDGE_INSET = 0.12
  const clampedScore = Math.min(100, Math.max(0, score))
  const markerBandIndex = Math.min(3, Math.floor(clampedScore / 25))
  const markerRawFraction = Math.min(1, Math.max(0, (clampedScore - markerBandIndex * 25) / 25))
  const markerFraction = MARKER_EDGE_INSET + markerRawFraction * (1 - MARKER_EDGE_INSET * 2)

  return (
    <>
      <ScreenContainer>
        <Card>
          <DSText variant="fluid-paragraph-300" weight="regular" color="secondary">
            Your score is
          </DSText>
          <DSSpacer size={8} direction="vertical" />
          <DSText variant="fluid-display-1000" weight="semiBold">
            {score}
          </DSText>
          <DSSpacer size={4} direction="vertical" />
          <DSText variant="fluid-paragraph-300" weight="regular" color="secondary">
            out of 100
          </DSText>
          <DSSpacer size={24} direction="vertical" />
          <BarRow>
            {SCORE_BAR_THRESHOLDS.map((b) => (
              <BarSegment key={b} $background={SCORE_BAR_COLOURS[b]} />
            ))}
            <BarMarker $bandIndex={markerBandIndex} $fraction={markerFraction} />
          </BarRow>
          <DSSpacer size={8} direction="vertical" />
          <BarLabelsRow>
            <DSText variant="fluid-label-100" weight="regular">
              Bad
            </DSText>
            <DSText variant="fluid-label-100" weight="regular">
              Excellent
            </DSText>
          </BarLabelsRow>
        </Card>
        <DSSpacer size={8} direction="vertical" />
        <DSText variant="fluid-label-100" weight="regular" align="center" color="placeholder">
          {disclaimer}
        </DSText>
        <DSSpacer size={32} direction="vertical" />
        <DSText variant="fluid-paragraph-300" weight="regular">
          Based on your answers, you have experienced a <b>{copy.headline}</b> over the past week.
        </DSText>
        <DSSpacer size={16} direction="vertical" />
        <DSText variant="fluid-paragraph-300" weight="regular">
          {copy.body}
        </DSText>
        <DSSpacer size={32} direction="vertical" />
        <DSText variant="fluid-heading-500" weight="semiBold">
          Your main symptoms
        </DSText>
        <DSSpacer size={16} direction="vertical" />
        {topSymptoms.map((symptom) => {
          const colours = SEVERITY_COLOURS[symptom.severity]
          return (
            <React.Fragment key={symptom.key}>
              <SymptomRow>
                <DSText variant="fluid-paragraph-300" weight="semiBold">
                  {symptom.label}
                </DSText>
                <SeverityBadge $background={colours.background} $color={colours.color}>
                  {symptom.severity}
                </SeverityBadge>
              </SymptomRow>
              <DSSpacer size={12} direction="vertical" />
            </React.Fragment>
          )
        })}
      </ScreenContainer>
      <StickyFooter>
        <StickyFooterContent>
          <DSButton
            variant="primary"
            colorScheme="default"
            size="large"
            width="full"
            onPress={() => (onNextClick as () => void)()}
            testId="continue-button"
          >
            <DSText variant="fluid-paragraph-200" weight="semiBold">
              {buttonText}
            </DSText>
          </DSButton>
        </StickyFooterContent>
      </StickyFooter>
    </>
  )
}
